import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import { useState, useContext } from 'react';
import { asyncHandler } from '../../utils/async-handler';
import { useNavigate } from 'react-router-dom';
import './login.styles.css';
import { IconButton, InputAdornment, Alert } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { initializeFirebase } from '../../firebase-push-notification';
import '../install/install.page';
import '../sign-up/sign-up.page';
import ModalComponent from '../../components/modal/modal.component';
import { resendVerificationEmail } from '../../services/userService';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import {
	CambiappContext,
	CambiappContextType,
} from '../../context/cambiapp.context';
import { RESET_PASSWORD_MODAL_CONTENT } from '../../constants';
import { resetPassword } from '../../services/userService';
import {
	Drawer,
	List,
	ListItem,
	ListItemText,
	ListItemButton,
	Collapse,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MenuIcon from '@mui/icons-material/Menu';
import { contactInfo } from '../../constants';

const Login = () => {
	const navigate = useNavigate();
	const [email, setEmail] = useState<string>();
	const [password, setPassword] = useState<string>();
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const handleEmailChange = (e: any) => setEmail(e.target.value);
	const handlePasswordChange = (e: any) => setPassword(e.target.value);
	const handleClickShowPassword = () => setShowPassword(!showPassword);
	const handleMouseDownPassword = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		event.preventDefault();
	};

	const styles = {
		root: {
			'& input:-webkit-autofill': {
				'-webkit-box-shadow': '0 0 0 100px #266798 inset !important',
				'background-clip': 'content-box !important',
			},
		},
	};

	const [isSidebarOpen, setSidebarOpen] = useState(false);
	const [isContactOpen, setContactOpen] = useState(false);

	const handleToggleContact = () => {
		setContactOpen(!isContactOpen);
	};

	const [modalOpen, setModalOpen] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalDescription, setModalDescription] = useState<
		string | React.ReactNode
	>('');
	const [showResend, setShowResend] = useState(false);

	const handleModalClose = () => {
		setModalOpen(false);
		setShowResend(false);
	};

	const handleResendEmail = async () => {
		try {
			await resendVerificationEmail();
		} catch (error) {
			console.error('Error resending verification email:', error);
		}
	};

	const [alertOpen, setAlertOpen] = useState(false);

	const { setUserStatus } = useContext(CambiappContext) as CambiappContextType;

	async function handleLogin() {
		if (email && password) {
			const normalizedEmail = email.toLowerCase().trim();

			initializeFirebase();
			const auth = getAuth();
			try {
				await signInWithEmailAndPassword(auth, normalizedEmail, password);
				setAlertOpen(false);
			} catch (error) {
				console.error('Invalid credentials:', error);
				setAlertOpen(true);
			}

			await asyncHandler.loginHandler(
				normalizedEmail,
				password,
				navigate,
				setModalOpen,
				setModalTitle,
				setModalDescription,
				setShowResend,
				setUserStatus,
			);
		}
	}

	const [passwordEmail, setPasswordEmail] = useState('');

	const [status, setStatus] = useState<'success' | 'error' | null>(null);

	const [modalResponse, setModalResponseOpen] = useState(false);

	const handleResetPassword = async () => {
		try {
			await resetPassword(passwordEmail);
			setStatus('success');
		} catch {
			setStatus('error');			
		}
		setModalResponseOpen(true);
	};

	const [passwordModalOpen, setPasswordModalOpen] = useState(false);

	const handlePasswordModalClose = () => {
		setPasswordModalOpen(false);
		setPasswordEmail('');
	};

	return (
		<Box className="login">
			<IconButton
				edge="start"
				color="inherit"
				aria-label="menu"
				onClick={() => setSidebarOpen(true)}
				sx={{ position: 'absolute', top: 20, left: 20 }}
			>
				<MenuIcon />
			</IconButton>
			<img src={'/cambiapp_logo.png'} alt="Logo" width={250} />
			<Box className="login-content">
				<TextField
					label="User"
					variant="outlined"
					onChange={handleEmailChange}
					type="email"
					InputProps={{
						sx: styles.root,
					}}
				/>
				<TextField
					label="Password"
					variant="outlined"
					onChange={handlePasswordChange}
					type={showPassword ? 'text' : 'password'}
					InputProps={{
						sx: styles.root,
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={handleClickShowPassword}
									onMouseDown={handleMouseDownPassword}
								>
									{showPassword ? <Visibility /> : <VisibilityOff />}
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
				{alertOpen && (
					<Alert severity="error">Email o contraseña inválidos.</Alert>
				)}
				<Button
					variant="contained"
					size="large"
					sx={{ textTransform: 'capitalize' }}
					onClick={handleLogin}
				>
					Login
				</Button>
				<Link href="/install" underline="hover" align="center">
					{'Instalar Cambiapp'}
				</Link>
				<Link
					component="button"
					onClick={() => {
						setPasswordModalOpen(true);
					}}
					underline="hover"
					align="center"
				>
					{'Olvidé mi contraseña'}
				</Link>
				<Link href="/sign-up" underline="hover" align="center">
					{'Crear Cuenta'}
				</Link>
				<ModalComponent
					open={modalOpen}
					title={modalTitle}
					description={modalDescription}
					imageSrc="/cambiapp_logo.png"
					onClose={handleModalClose}
					buttonText="Cerrar"
					onButtonClick={handleModalClose}
					showCloseButton
					secondaryButtonText={showResend ? 'Reenviar Email' : undefined}
					onSecondaryClick={showResend ? handleResendEmail : undefined}
				/>
				<ModalComponent
					open={passwordModalOpen}
					title={RESET_PASSWORD_MODAL_CONTENT.title}
					description={RESET_PASSWORD_MODAL_CONTENT.description}
					imageSrc="/cambiapp_logo.png"
					buttonText="Enviar"
					onClose={handlePasswordModalClose}
					onButtonClick={handleResetPassword}
					showCloseButton={true}
					secondaryButtonText="Cerrar"
					onSecondaryClick={handlePasswordModalClose}
					showTextField={true}
					textFieldLabel="Email"
					textFieldValue={passwordEmail}
					onTextFieldChange={setPasswordEmail}
				/>
				<ModalComponent
					open={modalResponse}
					title= {status === 'success' ? 'Email Enviado' : 'Error'}
					description={status === 'success' ? 'Por favor revisa tu casilla de correo' : 'No existe una cuenta creada con ese email'}
					imageSrc="/cambiapp_logo.png"
					buttonText="Cerrar"
					onButtonClick={() => setModalResponseOpen(false)}
					onClose={() => setModalResponseOpen(false)}
					showCloseButton={true}
				/>
			</Box>
			<Drawer
				anchor="left"
				open={isSidebarOpen}
				onClose={() => setSidebarOpen(false)}
			>
				<Box
					sx={{
						height: '100vh',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
						backgroundColor: 'inherit',
					}}
				>
					<List
						sx={{
							minWidth: '300px',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<ListItemButton onClick={handleToggleContact}>
							<ListItemText
								primary="Contacto"
								primaryTypographyProps={{
									style: {
										color: 'whitesmoke',
										fontSize: '24px',
										fontFamily: 'roboto',
									},
								}}
							/>
							{isContactOpen ? (
								<ExpandLess sx={{ color: 'white' }} />
							) : (
								<ExpandMore sx={{ color: 'white' }} />
							)}
						</ListItemButton>

						<Collapse in={isContactOpen} timeout="auto" unmountOnExit>
							<List component="div" disablePadding>
								<ListItem>
									<WhatsAppIcon sx={{ mr: 1, color: 'white' }} />
									<Link
										href={`https://wa.me/${contactInfo.cellphone}`}
										target="_blank"
										rel="noopener noreferrer"
										underline="hover"
										color="inherit"
									>
										{`+${contactInfo.cellphone}`}
									</Link>
								</ListItem>

								<ListItem>
									<MailOutlineIcon sx={{ mr: 1, color: 'white' }} />
									<Link
										href={`mailto:${contactInfo.email}`}
										underline="hover"
										color="inherit"
									>
										{contactInfo.email}
									</Link>
								</ListItem>
							</List>
						</Collapse>
					</List>
				</Box>
			</Drawer>
		</Box>
	);
};

export default Login;
